import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useI18n } from "@/i18n";

export const Overline = ({ children, className = "" }) => (
  <p className={`overline ${className}`}>{children}</p>
);

export const Section = ({ children, className = "", ...rest }) => (
  <section className={`mx-auto max-w-[1400px] px-5 md:px-10 ${className}`} {...rest}>
    {children}
  </section>
);

export const CTAButton = ({ to, onClick, children, testId, variant = "primary", className = "", type }) => {
  const base =
    "group inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold transition-colors";
  const styles =
    variant === "primary"
      ? "bg-black text-white hover:bg-zinc-700"
      : "border border-black/20 text-black hover:bg-zinc-50";
  const inner = (
    <>
      {children}
      <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
    </>
  );
  if (to)
    return (
      <Link to={to} data-testid={testId} className={`${base} ${styles} ${className}`}>
        {inner}
      </Link>
    );
  return (
    <button type={type || "button"} onClick={onClick} data-testid={testId} className={`${base} ${styles} ${className}`}>
      {inner}
    </button>
  );
};

export const PageHero = ({ overline, title, lead, image, children }) => (
  <div className="relative overflow-hidden border-b border-black/10 dt-noise">
    <div className="absolute inset-0 dt-grid-lines opacity-60" aria-hidden="true" />
    <Section className="relative grid gap-12 py-20 md:grid-cols-12 md:py-28">
      <div className="md:col-span-7 dt-reveal">
        <Overline>{overline}</Overline>
        <h1 className="mt-6 font-display text-5xl font-light leading-[0.95] tracking-tighter sm:text-6xl">
          {title}
        </h1>
        {lead && <p className="mt-8 max-w-xl text-base leading-relaxed text-zinc-600">{lead}</p>}
        {children && <div className="mt-10">{children}</div>}
      </div>
      {image && (
        <div className="md:col-span-5">
          <div className="relative aspect-[4/5] overflow-hidden border border-black/10 md:mt-8">
            <img src={image} alt="" loading="lazy" className="h-full w-full object-cover object-center" />
          </div>
        </div>
      )}
    </Section>
  </div>
);

const STATUS_TONE = {
  optimal: "bg-emerald-50 text-emerald-800 border-emerald-200",
  moderate: "bg-amber-50 text-amber-800 border-amber-200",
  altered: "bg-orange-50 text-orange-800 border-orange-200",
  critical: "bg-red-50 text-red-800 border-red-200",
};

export const StatusPill = ({ status, children, testId }) => {
  const { tb } = useI18n();
  return (
  <span
    data-testid={testId}
    className={`inline-block whitespace-nowrap border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] ${
      STATUS_TONE[status] || "border-black/15 bg-zinc-50 text-zinc-600"
    }`}
  >
    {children || tb(status)}
  </span>
  );
};

export const MetricRow = ({ metric, delay = 0 }) => (
  <div
    className="border-b border-black/10 py-5 dt-reveal"
    style={{ animationDelay: `${delay}ms` }}
    data-testid={`metric-${metric.key}`}
  >
    <div className="flex flex-wrap items-baseline justify-between gap-3">
      <p className="text-sm font-semibold">{metric.label}</p>
      <div className="flex items-center gap-4">
        <span className="font-mono-dt text-sm">
          {metric.value} <span className="text-zinc-400">{metric.unit}</span>
        </span>
        <StatusPill status={metric.status} />
      </div>
    </div>
    <div className="mt-3 h-[3px] w-full bg-zinc-100">
      <div
        className="h-full bg-black transition-all duration-700"
        style={{ width: `${metric.sub_score}%` }}
      />
    </div>
    <div className="mt-2 flex flex-wrap items-baseline justify-between gap-3">
      <p className="max-w-lg text-xs text-zinc-500">{metric.description}</p>
      <p className="font-mono-dt text-[10px] text-zinc-400">
        ref. {metric.reference} · {metric.sub_score}/100
      </p>
    </div>
    {metric.lab_comment ? (
      <p className="mt-2 border-l-2 border-black/10 pl-3 text-xs italic text-zinc-500">
        {metric.lab_comment}
      </p>
    ) : null}
  </div>
);

export const ScoreRing = ({ value, label, size = 168 }) => {
  const r = size / 2 - 8;
  const c = 2 * Math.PI * r;
  return (
    <div className="relative" style={{ width: size, height: size }} data-testid="score-ring">
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#EFEFF1" strokeWidth="6" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="#0A0A0A"
          strokeWidth="6"
          strokeDasharray={`${(c * value) / 100} ${c}`}
          style={{ transition: "stroke-dasharray 900ms cubic-bezier(0.16,1,0.3,1)" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-display text-4xl font-light leading-none">{value}</span>
        {label ? <span className="mt-1 overline text-[9px]">{label}</span> : null}
      </div>
    </div>
  );
};

export const Timeline = ({ steps, testId = "timeline" }) => (
  <ol data-testid={testId} className="relative">
    {steps.map((s, i) => (
      <li key={s.key} className="relative flex gap-5 pb-6 last:pb-0" data-testid={`step-${s.key}`}>
        {i < steps.length - 1 && (
          <span
            className={`absolute left-[9px] top-6 h-full w-[1px] ${s.done ? "bg-black" : "bg-black/15"}`}
            aria-hidden="true"
          />
        )}
        <span
          className={`relative z-10 mt-1 h-[18px] w-[18px] shrink-0 rounded-full border transition-colors ${
            s.current
              ? "border-black bg-white ring-4 ring-black/10"
              : s.done
              ? "border-black bg-black"
              : "border-black/20 bg-white"
          }`}
        />
        <div className="min-w-0">
          <p className={`text-sm ${s.done ? "font-semibold text-black" : "text-zinc-400"}`}>
            {s.label}
          </p>
          {s.at ? (
            <p className="mt-0.5 font-mono-dt text-[10px] text-zinc-400">
              {new Date(s.at).toLocaleString()}
            </p>
          ) : null}
        </div>
      </li>
    ))}
  </ol>
);

export const KPI = ({ label, value, sub, testId }) => (
  <div className="border border-black/10 bg-white p-5" data-testid={testId}>
    <p className="overline text-[9px]">{label}</p>
    <p className="mt-3 font-mono-dt text-2xl">{value}</p>
    {sub ? <p className="mt-1 text-xs text-zinc-500">{sub}</p> : null}
  </div>
);

// Le libellé posé au-dessus d'un simple trait ne montrait pas où écrire :
// la zone de saisie est désormais un cadre explicite, avec une hauteur
// confortable au pouce et un état de focus franc.
export const Field = ({ id, label, value, onChange, type = "text", testId, required = true, hint, placeholder }) => (
  <div>
    <label className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-500" htmlFor={id}>
      {label}
      {required ? <span className="ml-1 text-zinc-400">*</span> : null}
    </label>
    <input
      id={id}
      data-testid={testId}
      type={type}
      required={required}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="mt-2 h-12 w-full border border-black/15 bg-white px-4 text-base text-black outline-none transition-colors placeholder:text-zinc-300 hover:border-black/40 focus:border-black focus:ring-1 focus:ring-black"
    />
    {hint ? <p className="mt-1.5 text-[11px] text-zinc-400">{hint}</p> : null}
  </div>
);
